import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject,of  } from 'rxjs';
import { tap } from 'rxjs/operators';
import { authBackendEndPointToken } from '../app.config';
import { catchError, map } from 'rxjs/operators';
import { DisableCache } from '../loading.interceptor';
import { HttpContext } from '@angular/common/http';

export interface Event {
  id: string;
  title: string;
  description?: string;
  registrationUrl?: string;
  start: string;
  end: string;
  type: string;
  virtualPlatformType?: string;
  platform: string;
  meetingUrl?: string;
  externalAttendeeEmails?: string[];
  image?: string;
  participants: string[];
  organizationId?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt?: string;
  isActive: boolean;
  location?: string;
  maxAttendees?: number;
  tags: string[]; //
  isRecurring?: boolean;
  recurrenceFrequency?: string;
  targetOrganizationIds?: string[];
  recurrenceCount?: number;
  externalMeetingHostUrl?: string;
  meetingCode?: string;
  zoomStartUrl?: string;
   targetOrganizationNames?: string[];
}
export interface EventRegistrant {
  id: string;
  eventId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  role?: string;
  country?: string;
  status: string;
  registeredAt: string;
  organization?: string;
  platformRegistrationResult?: string;
}
export interface RegisterForEventDto {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  role?: string;
  country?: string;
}

export interface RegistrationResult {
  success: boolean;
  message: string;
  meetingUrl?: string;
}
  export interface OrgLite {
  id: string;
  name: string;
}

export interface EventResponse<T = Event[]> {
  success: boolean;
  message: string;
  data: T;
}
export interface EventDashboardStats {
  totalEvents: number;
  upcomingCount: number;
  pastCount: number;
  totalOrganizationsWithEvents: number;
  virtualEventCount: number;
  inPersonEventCount: number;
}

export interface PagedEventsResponse {
  items: Event[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}


@Injectable({
  providedIn: 'root'
})
export class EventsService {
  private backendUrl = inject(authBackendEndPointToken);
  private readonly URL = `${this.backendUrl}/api/publicevents`;

  // Subject to notify components when events change
  private eventsChangedSubject = new Subject<void>();
  public eventsChanged$ = this.eventsChangedSubject.asObservable();

  constructor(private http: HttpClient) {}


getDashboardStats(): Observable<EventDashboardStats> {
  return this.http.get<EventDashboardStats>(`${this.backendUrl}/api/event-dashboard/stats`);
}

getUpcomingEvents(limit = 10): Observable<Event[]> {
  return this.http.get<Event[]>(`${this.backendUrl}/api/event-dashboard/upcoming`, {
    params: { limit }
  });
}

getPastEventsPaged(page = 1, pageSize = 20): Observable<PagedEventsResponse> {
  return this.http.get<PagedEventsResponse>(`${this.backendUrl}/api/event-dashboard/past`, {
    params: { page, pageSize }
  });
}
getOrganizationsLightweight(): Observable<OrgLite[]> {
  return this.http.get<OrgLite[]>(`${this.backendUrl}/api/organization/lightweight`);
}
  getEvents(): Observable<EventResponse<Event[]>> {
    return this.http.get<EventResponse<Event[]>>(`${this.URL}`);
  }

  getEventById(id: string): Observable<EventResponse<Event>> {
    return this.http.get<EventResponse<Event>>(`${this.URL}/${id}`);
  }
registerForEvent(eventId: string, dto: RegisterForEventDto): Observable<EventResponse<RegistrationResult>> {
  return this.http.post<EventResponse<RegistrationResult>>(
    `${this.backendUrl}/api/events/${eventId}/register`,
    dto
  );
}
getRegistrants(eventId: string): Observable<EventResponse<EventRegistrant[]>> {
  return this.http.get<EventResponse<EventRegistrant[]>>(
    `${this.backendUrl}/api/events/${eventId}/registrants`
  );
}
  getEventsByParticipant(participants: string[]): Observable<EventResponse<Event[]>> {
    return this.http.post<EventResponse<Event[]>>(`${this.URL}/by-participants`, { participants });
  }
getVisibleEvents(): Observable<EventResponse<Event[]>> {
  return this.http.get<EventResponse<Event[]>>(
    `${this.URL}/visible`,
    {
      context: new HttpContext().set(DisableCache, true) // ✅ disable cache
    }
  ).pipe(
      catchError((error: any) => {
        console.error('Error fetching visible events:', error);
        return of({
          success: false,
          message: 'Error fetching visible events',
          data: []
        });
      })
    );
}
  getEventsByParticipants(roles: string[]): Observable<EventResponse<Event[]>> {
    let params = new HttpParams();
    if (roles && !roles.includes('system admin')) {
      roles.forEach(role => {
        params = params.append('roles', role);
      });
    }
    return this.http.get<EventResponse<Event[]>>(`${this.URL}/participant/one`, { params });
  }

  createEvent(eventData: Partial<Event>): Observable<EventResponse<Event>> {
    return this.http.post<EventResponse<Event>>(`${this.URL}`, eventData).pipe(
      tap(() => this.eventsChangedSubject.next())
    );
  }

  updateEvent(id: string, eventData: Partial<Event>): Observable<EventResponse<Event>> {
    return this.http.put<EventResponse<Event>>(`${this.URL}/${id}`, eventData).pipe(
      tap(() => this.eventsChangedSubject.next())
    );
  }

  deleteEvent(id: string): Observable<EventResponse<null>> {
    return this.http.delete<EventResponse<null>>(`${this.URL}/${id}`).pipe(
      tap(() => this.eventsChangedSubject.next())
    );
  }
}
